import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MasterSelect from "./MasterSelect";
import MasterDataMultiSelect from "@/components/MasterDataMultiSelect";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function BrokerFormFields({ form, set, showOwner = true }) {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role];
  const isAdmin = roles.includes("admin");
  const isSMOnly = roles.includes("sourcing_manager") && !roles.includes("admin") && !roles.includes("relationship_manager");
  const { data: users = [] } = useQuery({
    queryKey: ["report-users"],
    queryFn: async () => (await api.get("/auth/users")).data,
    enabled: isAdmin && showOwner,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Broker Name *</Label>
          <Input value={form.broker_name || ""} onChange={(e) => set("broker_name", e.target.value)} className="mt-1" data-testid="broker-name" />
        </div>
        <div>
          <Label>Firm Name</Label>
          <Input value={form.firm_name || ""} onChange={(e) => set("firm_name", e.target.value)} className="mt-1" data-testid="broker-firm" />
        </div>
        <div>
          <Label>Contact No.</Label>
          <Input value={form.contact_no || ""} onChange={(e) => set("contact_no", e.target.value)} className="mt-1" data-testid="broker-contact" />
        </div>
        <div>
          <Label>Alternate No.</Label>
          <Input value={form.alternate_no || ""} onChange={(e) => set("alternate_no", e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label>Email</Label>
          <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="broker-email" />
        </div>
        <div>
          <Label>Broker Type</Label>
          <div className="mt-1"><MasterSelect listType="broker_type" value={form.broker_type_id} onChange={(v) => set("broker_type_id", v)} placeholder="Type…" testId="broker-type" filterLabels={isSMOnly ? ["Sourcing/Supply Broker", "Both"] : null} /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Specialization Categories</Label>
          <div className="mt-1"><MasterDataMultiSelect listType="asset_category" values={form.specialization_category_ids || []} onChange={(v) => set("specialization_category_ids", v)} placeholder="Categories…" testId="broker-spec-cat" /></div>
        </div>
        <div>
          <Label>Specialization Types</Label>
          <div className="mt-1"><MasterDataMultiSelect listType="asset_type" values={form.specialization_type_ids || []} onChange={(v) => set("specialization_type_ids", v)} placeholder="Types…" testId="broker-spec-type" /></div>
        </div>
        <div className="col-span-2">
          <Label>Coverage Cities</Label>
          <div className="mt-1"><MasterDataMultiSelect listType="city" values={form.coverage_city_ids || []} onChange={(v) => set("coverage_city_ids", v)} placeholder="Cities…" testId="broker-cities" /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Commission Split Type</Label>
          <div className="mt-1"><MasterSelect listType="commission_split_type" value={form.commission_split_type_id} onChange={(v) => set("commission_split_type_id", v)} placeholder="Split type…" testId="broker-split-type" /></div>
        </div>
        <div>
          <Label>Commission Split Rate</Label>
          <Input type="number" value={form.commission_split_rate ?? ""} onChange={(e) => set("commission_split_rate", e.target.value === "" ? null : Number(e.target.value))} className="mt-1 font-mono-num" data-testid="broker-split-rate" />
        </div>
        {showOwner && isAdmin && (
          <div>
            <Label>Relationship Owner</Label>
            <Select value={form.relationship_owner_id || ""} onValueChange={(v) => set("relationship_owner_id", v)}>
              <SelectTrigger className="mt-1" data-testid="broker-owner"><SelectValue placeholder="Owner…" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Tags</Label>
          <div className="mt-1"><MasterDataMultiSelect listType="tags" values={form.tag_ids || []} onChange={(v) => set("tag_ids", v)} placeholder="Tags…" allowAdd testId="broker-tags" /></div>
        </div>
      </div>

      <div>
        <Label>Short Notes</Label>
        <Textarea value={form.short_notes || ""} onChange={(e) => set("short_notes", e.target.value)} className="mt-1" rows={2} data-testid="broker-notes" />
      </div>
    </div>
  );
}
